import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Mail, Users, DollarSign, Play, ArrowLeft, BookOpen, UsersRound } from 'lucide-react';
import DemoVideoModal from '../components/DemoVideoModal';
import TutorialVideoModal from '../components/TutorialVideoModal';

const INTERACTIVE_TUTORIALS = [
  {
    key: 'signatures',
    title: 'Professional Email Signatures',
    description: 'Pick a template, fill your info, let AI build a conversion-optimised signature, and install in Gmail/Outlook in under a minute.',
    icon: Mail,
    accent: 'from-blue-500 to-cyan-500',
  },
  {
    key: 'networking',
    title: 'Referral Networking',
    description: 'Search complementary pros in your area, see AI-matched partners, send connection requests, and chat to close referrals.',
    icon: Users,
    accent: 'from-emerald-500 to-teal-500',
  },
  {
    key: 'marketplace',
    title: 'Marketplace & Advertising',
    description: 'Browse ad slots on other pros\' signatures, request placement with your headline, and watch your ad go live in their inbox sends.',
    icon: DollarSign,
    accent: 'from-amber-500 to-orange-500',
  },
];

// Video-based tutorials (MP4 stored in MongoDB GridFS). Add more entries
// here to expose additional tutorial videos.
const VIDEO_TUTORIALS = [
  {
    key: 'groups-create',
    title: 'Create a Group',
    description: 'Walkthrough of creating a professional group in ConnectMail — set up the name, description, privacy, and invite your first members.',
    icon: UsersRound,
    accent: 'from-fuchsia-500 to-pink-500',
    video_url: '/api/tutorials/video/tutorial-groups-v1',
  },
];

export default function TutorialsPage() {
  const [activeInteractiveKey, setActiveInteractiveKey] = useState(null);
  const [activeVideoTutorial, setActiveVideoTutorial] = useState(null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100" data-testid="tutorials-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-slate-900 font-['Outfit']">Product Tutorials</h1>
              <p className="text-xs text-slate-500">Quick interactive walkthroughs to learn ConnectMail</p>
            </div>
          </div>
          <Link to="/dashboard">
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="tutorials-back-to-dashboard">
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to dashboard
            </Button>
          </Link>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
        {/* Interactive walkthroughs */}
        <section>
          <div className="mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Outfit']">
              Interactive walkthroughs
            </h2>
            <p className="text-sm md:text-base text-slate-600 mt-2 max-w-2xl">
              Soundless, captioned walk-throughs — under a minute each.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5" data-testid="tutorial-grid">
            {INTERACTIVE_TUTORIALS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveInteractiveKey(t.key)}
                  className="group text-left bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:border-slate-300 transition-all"
                  data-testid={`tutorial-card-${t.key}`}
                >
                  <div className={`relative h-32 flex items-center justify-center bg-gradient-to-br ${t.accent}`}>
                    <div className="relative w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 text-slate-700 fill-current ml-0.5" />
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon className="w-4 h-4 text-slate-500" />
                      <h3 className="text-base font-bold text-slate-900 font-['Outfit']">{t.title}</h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-snug">{t.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Recorded video tutorials */}
        {VIDEO_TUTORIALS.length > 0 && (
          <section data-testid="video-tutorials-section">
            <div className="mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Outfit']">
                Recorded video tutorials
              </h2>
              <p className="text-sm md:text-base text-slate-600 mt-2 max-w-2xl">
                Real screen-recordings showing exactly how to complete common tasks.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-5" data-testid="video-tutorial-grid">
              {VIDEO_TUTORIALS.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.key}
                    onClick={() => setActiveVideoTutorial(t)}
                    className="group text-left bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:border-slate-300 transition-all"
                    data-testid={`video-tutorial-card-${t.key}`}
                  >
                    <div className={`relative h-32 flex items-center justify-center bg-gradient-to-br ${t.accent}`}>
                      <div className="relative w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                        <Play className="w-6 h-6 text-slate-700 fill-current ml-0.5" />
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon className="w-4 h-4 text-slate-500" />
                        <h3 className="text-base font-bold text-slate-900 font-['Outfit']">{t.title}</h3>
                      </div>
                      <p className="text-sm text-slate-600 leading-snug">{t.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {activeInteractiveKey && (
        <DemoVideoModal demoKey={activeInteractiveKey} onClose={() => setActiveInteractiveKey(null)} />
      )}
      {activeVideoTutorial && (
        <TutorialVideoModal tutorial={activeVideoTutorial} onClose={() => setActiveVideoTutorial(null)} />
      )}
    </div>
  );
}
